import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { StatusProvider, useStatus } from './contexts/StatusContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import SubscriptionPaywall from './components/Subscription/SubscriptionPaywall';
import RegistrationFlow from './components/Auth/RegistrationFlow';
import ModernDashboard from './components/Dashboard/ModernDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import FamilyJoinPage from './pages/FamilyJoinPage';
import FamilyMembersManager from './components/family/FamilyMembersManager';
import FamilyPINLogin from './components/Auth/FamilyPINLogin';
import KidTaskView from './components/tasks/KidTaskView';
import FamiliesLanding from './components/landing/FamiliesLanding';
import './styles/tailwind.output.css';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { Auth_Status } = useStatus();
  return Auth_Status ? children : <Navigate to="/" replace />;
};

// Landing page or redirect for authenticated users
const LandingOrRedirect = () => {
  const { Auth_Status } = useStatus();
  const navigate = useNavigate();

  if (Auth_Status) {
    return <Navigate to="/tasks" replace />;
  }

  return (
    <FamiliesLanding
      onGetStarted={() => navigate('/login')}
      onEmailCapture={(email) => {
        console.log('Email captured:', email);
        navigate('/login');
      }}
    />
  );
};

const AppRoutes = () => {
  const subscription = useSubscription();

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '12px',
          },
          success: {
            style: {
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            },
          },
          error: {
            style: {
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            },
          },
        }}
      />

      <Routes>
        <Route path="/" element={<LandingOrRedirect />} />
        <Route path="/login" element={<RegistrationFlow />} />
        <Route path="/signup" element={<RegistrationFlow />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/join" element={<FamilyJoinPage />} />
        <Route path="/family-login" element={<FamilyPINLogin />} />
        <Route path="/kid-tasks" element={<KidTaskView />} />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <ModernDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/family-members"
          element={
            <ProtectedRoute>
              <FamilyMembersManager />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Subscription Paywall Modal */}
      <SubscriptionPaywall
        isOpen={subscription.showUpgradeModal}
        onClose={subscription.closeUpgradeModal}
        onUpgrade={subscription.upgradeTier}
        currentTier={subscription.subscriptionTier}
        upgradeMessage={subscription.getUpgradeMessage()}
      />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <StatusProvider>
        <SubscriptionProvider>
          <WebSocketProvider autoConnect={true}>
            <AppRoutes />
          </WebSocketProvider>
        </SubscriptionProvider>
      </StatusProvider>
    </BrowserRouter>
  );
}

export default App;
